import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, Users, FileText, Award,
  RefreshCw, BarChart3, PieChart, Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend,
  LineChart, Line, Area, AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadAnalytics();
  }, [token, navigate]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subvalue, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {subvalue && <div className="text-xs text-gray-500 mt-1">{subvalue}</div>}
    </div>
  );

  const LEVEL_COLORS = ['#CD7F32', '#C0C0C0', '#FFD700', '#E5E4E2', '#38B840'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Analytics Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={loadAnalytics}
              className="text-white border-white/20 hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <span className="text-white/50 text-xs md:text-sm hidden sm:block">{getVersionString()}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            icon={FileText} 
            label="Gesamte Fälle" 
            value={data?.summary?.total_cases || 0}
            subvalue={`+${data?.summary?.recent_cases_30d || 0} in 30 Tagen`}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard 
            icon={Users} 
            label="Partner" 
            value={data?.summary?.total_partners || 0}
            subvalue={`+${data?.summary?.recent_partners_30d || 0} in 30 Tagen`}
            color="bg-green-100 text-green-600"
          />
          <StatCard 
            icon={Award} 
            label="Abgeschlossen" 
            value={data?.summary?.total_completed || 0}
            subvalue={`${data?.summary?.completion_rate || 0}% Quote`}
            color="bg-emerald-100 text-emerald-600"
          />
          <StatCard 
            icon={TrendingUp} 
            label="Trend (30 Tage)" 
            value={`${data?.summary?.recent_cases_30d || 0}`}
            subvalue="Neue Fälle"
            color="bg-purple-100 text-purple-600"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Monthly Cases Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" />
              Fälle pro Monat
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data?.monthly_cases || []}>
                  <defs>
                    <linearGradient id="colorCases" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month_short" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.month || label}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="cases" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    fill="url(#colorCases)" 
                    name="Fälle"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-amber-500" />
              Status-Verteilung
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={data?.status_distribution || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {(data?.status_distribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Partner Levels Distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              Partner nach Provisions-Stufe
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.partner_levels || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#9CA3AF" width={70} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }} />
                  <Bar dataKey="count" name="Partner" radius={[0, 4, 4, 0]}>
                    {(data?.partner_levels || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={LEVEL_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Partners */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Top 10 Partner
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(data?.top_partners || []).map((partner, index) => (
                <div 
                  key={partner.partner_id} 
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white`}
                         style={{ backgroundColor: LEVEL_COLORS[partner.level - 1] || '#6B7280' }}>
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 text-sm truncate max-w-[200px]">
                        {partner.company_name}
                      </div>
                      <div className="text-xs text-gray-500">{partner.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">{partner.case_count}</div>
                    <div className="text-xs text-gray-500">Fälle</div>
                  </div>
                </div>
              ))}
              {(!data?.top_partners || data.top_partners.length === 0) && (
                <div className="text-center text-gray-500 py-8">
                  Keine Partner-Daten verfügbar
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Monthly Revenue Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-500" />
            Monatlicher Umsatz (Abgeschlossene Fälle)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.monthly_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month_short" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" tickFormatter={(v) => `${v}€`} />
                <Tooltip 
                  contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.month || label}
                  formatter={(value) => [`${value.toLocaleString('de-DE')}€`, 'Umsatz']}
                />
                <Bar dataKey="revenue" name="Umsatz" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
